import 'react-image-crop/dist/ReactCrop.css';

import { useAutoFrameZoomView } from '@src/hooks/useAutoFrameZoomView';
import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import { useReframeSpeakersTab } from '@src/hooks/useReframeSpeakersTab';
import { useUnmappedSpeakerApply } from '@src/hooks/useUnmappedSpeakerApply';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import AiProducerIcon from '@src/svgs/AiProducerIcon';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import { findOriginalVideoElementAtCurrentTime } from '@src/utility/elements';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import Checkbox from '../common/Checkbox';
import { DialogTitle } from '../common/dialogStyles';
import Modal from '../common/Modal';
import Tabs from '../common/Tabs';
import { MODAL_TOOLTIP_STYLE } from '../common/tooltipStyles';
import ReactCrop from '../react-image-multi-crop';
import { ReframeSpeakersTab } from './reframe-video-modal/ReframeSpeakersTab';
import { TabPanel, TabPanels } from './reframe-video-modal/tabPanels';
import { UnmappedSpeakerCheckbox } from './reframe-video-modal/UnmappedSpeakerCheckbox';
import { useReframeVideoModal } from './reframe-video-modal/useReframeVideoModal';
import {
  countScenesFramedDifferently,
  type FitOption,
  framingCautionApplies,
  getSlotColor,
  type SlotColorStyle,
} from './reframe-video-modal/utils';
import { ReframeAspectDropdown } from './ReframeAspectDropdown';

/**
 * What applying to all costs, said plainly. Shown at every output ratio: the
 * number of scenes it rewrites is worth knowing whatever the video is shaped
 * like. The Auto-Framing caveat on top of it is not — see `CautionMarker`.
 */
const replacedFramingImpact = (sceneCount: number): string =>
  `Replaces framing on ${sceneCount} ${sceneCount === 1 ? 'scene' : 'scenes'}.`;

/** What the marker adds over the line above, where per-speaker framing lives. */
type ReframeTab = 'framing' | 'speakers';
const REFRAME_TABS: Array<{ id: ReframeTab; label: string }> = [
  { id: 'framing', label: 'Framing' },
  { id: 'speakers', label: 'Speakers' },
];

const AUTO_FRAMING_CAUTION =
  'The new framing overwrites speaker auto-framing for each scene.';

export const ReframeVideoModal: React.FC = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = videoCreator.rootStore.editorStore;

  const {
    showAutoFrameZoomView,
    autoFrameDisabledReason,
    handleAutoFrameZoomView,
    appliedVersion,
  } = useAutoFrameZoomView({
    editor: editorStore?.editor,
    aspectRatio: videoCreator.getCurrentAspectRatio(),
    storyId: videoCreator.story?.id,
    shots: videoCreator.sceneDetections,
    // Scene commands only mark the editor dirty; refresh so the layout index is
    // rebuilt and the canvas repaints immediately.
    onApplied: useCallback(() => {
      editorStore?.refreshTimeline(videoCreator.time);
    }, [editorStore, videoCreator]),
  });

  // Read fresh every render: `appliedVersion` state changes after Auto-Frame
  // Zoom View replaces the scenes, so this picks up the new array.
  const scenes = editorStore?.editor?.timeline?.scenes ?? [];
  const [activeTab, setActiveTab] = useState<ReframeTab>('framing');
  const unmappedSpeaker = useUnmappedSpeakerApply(
    editorStore?.editor,
    videoCreator.getCurrentAspectRatio(),
  );

  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const editorAudioUrl = videoCreator.story?.transcription?.audio?.url;

  // Build specific dependencies for the hook
  const deps = useMemo(
    () => ({
      time: videoCreator.time,
      defaultVideoAspectRatio: videoCreator.getDefaultAspectRatio(),
      currentVideoAspectRatio: videoCreator.getCurrentAspectRatio(),
      hasCurrentVideo: !!videoCreator.currentVideo,
      scenes,
      sceneLayoutIndexStore: videoCreator.rootStore.sceneLayoutIndexStore,
      sceneLayoutSelectionManager: videoCreator.sceneLayoutSelectionManager,
      rendererSyncService:
        videoCreator.rootStore.editorStore.rendererSyncService ?? null,
      findElementById: (id: string) =>
        videoCreator.rootStore.videoSourceStore.findElementById(id),
      isOriginalVideoElement: (element: Record<string, unknown>) =>
        videoCreator.isOriginalVideoElement(element),
      findOriginalVideoElementAtCurrentTime: () =>
        findOriginalVideoElementAtCurrentTime(videoCreator),
      setActiveElements: (elementId: string) =>
        videoCreator.setActiveElements(elementId),
      closeModal: () => {
        setActiveTab('framing');
        videoCreator.setOpenReframeVideoModal(false);
      },
      unmappedSpeaker,
      localPreviewUrl,
      editorAudioUrl,
    }),
    [
      videoCreator,
      videoCreator.time,
      scenes,
      unmappedSpeaker,
      localPreviewUrl,
      editorAudioUrl,
    ],
  );

  const {
    videoSource,
    videoTime,
    isResetDisabled,
    videoRef,
    activeSlot,
    orderedSlots,
    currentFit,
    activeSlotIndex,
    displayCrops,
    displayCrop,
    canChangeAspectRatio,
    defaultVideoAspectRatio,
    setDefaults,
    handleAspectRatioChange,
    handleCropChange,
    handleMultiCropChange,
    handleClose,
    handleSlotSelect,
    handleApply,
    applyToAllScenes,
    setApplyToAllScenes,
    applyToUnmappedSpeaker,
    setApplyToUnmappedSpeaker,
    unmappedSpeakerOffer,
    syncVideoSourceAndTime,
  } = useReframeVideoModal(deps);

  // Re-derive the Framing tab's slots from the scenes a face assignment just
  // reframed. Deferred to an effect: `scenes` only carries them on the render
  // that follows.
  const [speakersAppliedVersion, setSpeakersAppliedVersion] = useState(0);
  useEffect(() => {
    if (speakersAppliedVersion > 0) setDefaults();
  }, [speakersAppliedVersion]);

  const speakersTab = useReframeSpeakersTab({
    active: activeTab === 'speakers',
    editor: editorStore?.editor,
    aspectRatio: videoCreator.getCurrentAspectRatio(),
    sceneId: orderedSlots[0]?.sceneId,
    rendererSyncService: editorStore?.rendererSyncService ?? null,
    onAfterApply: useCallback(() => {
      editorStore?.refreshTimeline(videoCreator.time);
      setSpeakersAppliedVersion(version => version + 1);
    }, [editorStore, videoCreator]),
  });

  /**
   * Scenes carrying a framing of their own, which applying to all replaces.
   * Zero when every scene already shares this framing — nothing is lost there,
   * and a warning that cries wolf gets clicked through.
   */
  const currentAspectRatio = videoCreator.getCurrentAspectRatio();
  const scenesFramedDifferently = useMemo(
    () => countScenesFramedDifferently(scenes, orderedSlots),
    [scenes, orderedSlots],
  );

  /**
   * The Auto-Framing caveat, only where per-speaker framing is the norm. The
   * count itself is said at every ratio; this is the extra the marker carries.
   */
  const showsAutoFramingCaution =
    applyToAllScenes &&
    scenesFramedDifferently > 0 &&
    framingCautionApplies(currentAspectRatio);

  /** Per instance — a shared id fires every mounted tooltip off one hover. */
  const cautionTooltipId = useId();

  const activeSlotColor = getSlotColor(activeSlotIndex);

  const frameStyle = useMemo<SlotColorStyle>(
    () => ({
      '--slot-color': activeSlotColor,
    }),
    [activeSlotColor],
  );

  const cropStyleResolver = useCallback(
    (index: number) => {
      const slotColor = getSlotColor(index);
      const isActive = index === activeSlotIndex;

      return {
        '--slot-color': slotColor,
        boxShadow: isActive
          ? `0 0 0 2px ${slotColor}`
          : '0 0 0 1px rgba(3, 4, 26, 0.5)',
        transition: 'box-shadow var(--transition-normal) ease',
      };
    },
    [activeSlotIndex],
  );

  useEffect(() => {
    if (
      !videoCreator.openReframeVideoModal ||
      !videoCreator?.currentVideo?.videoSource
    )
      return;
    setDefaults();
  }, [videoCreator.openReframeVideoModal]);

  // Re-derive the slots from the rewritten scenes. Deferred to an effect
  // because `setDefaults` reads through `scenes`, which only updates on the
  // render that follows the version bump.
  const setDefaultsRef = useRef(setDefaults);
  setDefaultsRef.current = setDefaults;

  useEffect(() => {
    if (appliedVersion === 0) return;
    setDefaultsRef.current();
  }, [appliedVersion]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = videoTime;
    }
  }, [videoTime, videoRef]);

  useEffect(() => {
    syncVideoSourceAndTime();
  }, [syncVideoSourceAndTime]);

  const aspectRatioForContainer = defaultVideoAspectRatio;

  if (!videoCreator.openReframeVideoModal) {
    return null;
  }

  return (
    <Modal
      isOpen={videoCreator.openReframeVideoModal}
      closeModal={handleClose}
      contentStyle={{
        minWidth: '600px',
        maxWidth: '1600px',
        maxHeight: '1000px',
        overflow: 'hidden',
      }}
    >
      <Wrapper data-testid="reframe-video-modal">
        <ModalHeading>Reframing</ModalHeading>
        <SubHeading>Set your framing</SubHeading>
        <TabsRow>
          <Tabs
            tabs={REFRAME_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </TabsRow>
        <TabPanels>
          <TabPanel
            $hidden={activeTab !== 'framing'}
            aria-hidden={activeTab !== 'framing'}
          >
            <ControlsRow>
              {canChangeAspectRatio && (
                <ReframeAspectDropdown
                  value={activeSlot?.selectedAspectRatio ?? 'original'}
                  onChange={handleAspectRatioChange}
                />
              )}
              {orderedSlots.length > 1 && (
                <SlotSelector role="tablist" aria-label="Layout slots">
                  {orderedSlots.map((slot, index) => {
                    const slotColor = getSlotColor(index);
                    return (
                      <SlotButton
                        key={slot.id}
                        type="button"
                        $active={slot.id === activeSlot?.id}
                        $color={slotColor}
                        onClick={() => handleSlotSelect(slot.id)}
                        role="tab"
                        aria-selected={slot.id === activeSlot?.id}
                      >
                        {slot.slotName || `Slot ${index + 1}`}
                      </SlotButton>
                    );
                  })}
                </SlotSelector>
              )}
              {showAutoFrameZoomView && (
                <AutoFrameButton
                  type="button"
                  onClick={handleAutoFrameZoomView}
                  disabled={!!autoFrameDisabledReason}
                  title={autoFrameDisabledReason ?? ''}
                >
                  <AiProducerIcon
                    strokeColor="currentColor"
                    width="16"
                    height="16"
                    viewBox="0 0 22 20"
                  />
                  Auto-Frame Zoom View
                </AutoFrameButton>
              )}
            </ControlsRow>
            <ModalContent>
              <VideoFrameContainer
                aspectRatio={aspectRatioForContainer || '16:9'}
                style={frameStyle}
              >
                {activeSlotIndex >= 0 && (
                  <ReactCrop
                    crop={displayCrop}
                    crops={displayCrops}
                    activeCropIndex={
                      activeSlotIndex >= 0 ? activeSlotIndex : undefined
                    }
                    onActiveCropChange={index => {
                      const nextSlot = orderedSlots[index];
                      if (nextSlot) {
                        handleSlotSelect(nextSlot.id);
                      }
                    }}
                    onChange={handleCropChange}
                    onChangeCrops={handleMultiCropChange}
                    getCropStyleForIndex={cropStyleResolver}
                    cropAspects={orderedSlots.map(slot => slot.aspectRatio)}
                    minHeight={100}
                    minWidth={100}
                  >
                    <VideoPreviewWrapper
                      aspectRatio={aspectRatioForContainer || '16:9'}
                    >
                      <VideoPreviewContainer
                        fit={currentFit}
                        src={videoSource}
                        autoPlay={false}
                        controls={false}
                        ref={videoRef}
                      />
                    </VideoPreviewWrapper>
                  </ReactCrop>
                )}
              </VideoFrameContainer>
            </ModalContent>
            {scenes.length > 1 && (
              <ApplyAllRow>
                {unmappedSpeakerOffer && unmappedSpeaker && (
                  <UnmappedSpeakerCheckbox
                    offer={unmappedSpeakerOffer}
                    checked={applyToUnmappedSpeaker}
                    onChange={setApplyToUnmappedSpeaker}
                    onLinkSpeaker={unmappedSpeaker.linkSpeaker}
                  />
                )}
                <Checkbox
                  label="Apply framing to all scenes"
                  checked={applyToAllScenes}
                  color={
                    applyToAllScenes ? 'var(--accent)' : 'var(--background)'
                  }
                  borderColor={
                    applyToAllScenes ? 'var(--accent)' : 'var(--border)'
                  }
                  onClick={() => setApplyToAllScenes(!applyToAllScenes)}
                  overrides={{ containerStyles: { 'align-items': 'center' } }}
                />
                {showsAutoFramingCaution && (
                  <>
                    <CautionMarker
                      data-tooltip-id={cautionTooltipId}
                      data-tooltip-content={AUTO_FRAMING_CAUTION}
                      aria-label={AUTO_FRAMING_CAUTION}
                    >
                      <CircleExclammationIcon
                        width="13"
                        height="13"
                        strokeColor="currentColor"
                        strokeWidth={1.75}
                      />
                    </CautionMarker>
                    <Tooltip
                      id={cautionTooltipId}
                      style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '280px' }}
                    />
                  </>
                )}
                {/* One line, always, so ticking the box never grows the row and
                resizes the centred modal. */}
                <ApplyAllHint>
                  {applyToAllScenes && scenesFramedDifferently > 0
                    ? replacedFramingImpact(scenesFramedDifferently)
                    : null}
                </ApplyAllHint>
              </ApplyAllRow>
            )}
            <Buttons>
              <OutlineButton
                // Called with no args: `setDefaults` takes options, and passing it
                // straight to onClick would hand it the click event as them.
                onClick={() => setDefaults()}
                disabled={!videoSource || isResetDisabled}
              >
                Reset
              </OutlineButton>
              <PrimaryButton
                // Called with no args: passing `handleApply` straight to onClick
                // would hand it the click event as its options object.
                onClick={() => handleApply()}
                disabled={!videoSource}
              >
                Apply
              </PrimaryButton>
            </Buttons>
          </TabPanel>
          <TabPanel
            $hidden={activeTab !== 'speakers'}
            aria-hidden={activeTab !== 'speakers'}
          >
            <ReframeSpeakersTab view={speakersTab} />
            <Buttons>
              <PrimaryButton
                onClick={handleClose}
                // A face assignment is saving: closing now would drop the
                // reframe that follows it.
                disabled={speakersTab.saveStatus === 'saving'}
              >
                {speakersTab.saveStatus === 'saving' ? 'Saving…' : 'Done'}
              </PrimaryButton>
            </Buttons>
          </TabPanel>
        </TabPanels>
      </Wrapper>
    </Modal>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 48px 24px;
  border-radius: 16px;
  border: 1px solid var(--border);
  box-sizing: border-box;
  background: var(--background);
`;

const TabsRow = styled.div`
  width: 100%;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 8px;
`;

/**
 * Above the buttons rather than beside them: it changes what Apply does, and
 * a creator who reads the button first has to have passed it.
 */
const ApplyAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  margin-top: -12px;
`;

/**
 * Reserved whether or not there is anything to say, so ticking the box never
 * changes the row's height — and one line only, for the same reason.
 */
const ApplyAllHint = styled.span`
  min-height: var(--leading-text-xs);
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/**
 * Amber, because it marks what the Apply takes away rather than what it does.
 * A marker rather than a line of copy: the caveat is only relevant while the
 * box is ticked, and text that comes and goes resizes a centred modal.
 */
const CautionMarker = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: var(--warning, #b58105);
  cursor: help;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
`;

const SlotSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const AutoFrameButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  border-radius: var(--radius-button);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const SlotButton = styled.button<{ $active: boolean; $color: string }>`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid
    ${props => (props.$active ? props.$color : 'rgba(72, 72, 72, 0.8)')};
  background: ${props => (props.$active ? props.$color : 'transparent')};
  color: ${props =>
    props.$active ? 'var(--background)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.6px;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background var(--transition-normal) ease,
    border-color var(--transition-normal) ease,
    color var(--transition-normal) ease;

  &:hover {
    border-color: ${props => props.$color};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.$color};
    outline-offset: 2px;
  }
`;

const OutlineButton = styled.button`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  cursor: pointer;

  background: var(--surface);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  line-height: normal;

  border-radius: 8px;
  border: 1px solid var(--border);
  transition: var(--transition-fast);

  &:hover {
    border-color: var(--primary);
  }

  &:disabled {
    color: var(--border);
    border: 1px solid var(--border);
    cursor: not-allowed;
  }

  &.discard-btn {
    margin-top: 8px;
  }
`;

const PrimaryButton = styled(OutlineButton)`
  background: var(--primary);
  color: var(--background);
  border: none;
  font-weight: 600;
  width: 100%;

  &:hover {
    background: var(--primary-hover);
    border-color: transparent;
  }
`;

const ModalHeading = styled(DialogTitle)`
  strong {
    font-weight: var(--font-weight-bold);
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: max-content;
`;

const SubHeading = styled.p`
  color: var(--foreground);
  text-align: center;
  font-family: var(--font-primary);
  font-size: 32px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 120%;
  margin: 0;
`;

const VideoFrameContainer = styled.div<{ aspectRatio: string }>`
  width: 100%;
  height: 350px;
  aspect-ratio: ${props => props.aspectRatio.replace(':', ' / ')};
  display: flex;
  justify-content: center;
  position: relative;
  overflow: visible;
  background: #000;

  .ReactCrop {
    width: 100%;
    height: 100%;
  }

  @keyframes marching-ants {
    0% {
      background-position:
        0px 0px,
        0px 100%,
        0px 0px,
        100% 0px;
    }
    100% {
      background-position:
        16px 0px,
        -16px 100%,
        0px 16px,
        100% -16px;
    }
  }

  .ReactCrop__crop-selection:not(
    .ReactCrop--no-animate .ReactCrop__crop-selection
  ) {
    background-image:
      linear-gradient(
        to right,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      ),
      linear-gradient(
        to right,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      ),
      linear-gradient(
        to bottom,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      ),
      linear-gradient(
        to bottom,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      );
    background-size:
      16px 2px,
      16px 2px,
      2px 16px,
      2px 16px !important;
    background-position:
      0px 0px,
      0px 100%,
      0px 0px,
      100% 0px;
    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y !important;
    animation: marching-ants 1s linear infinite;
  }

  .ReactCrop__drag-handle {
    background-color: var(--slot-color, var(--primary));
    border: none;
    width: 10px;
    height: 10px;
    margin: 2px;
    border-radius: 50%;
  }
`;

const VideoPreviewWrapper = styled.div<{ aspectRatio: string }>`
  height: 350px;
  aspect-ratio: ${props => props.aspectRatio.replace(':', ' / ')};
  position: relative;
  overflow: visible;
`;

const VideoPreviewContainer = styled.video<{ fit: FitOption }>`
  width: 100%;
  height: 100%;
  position: absolute;
  border-radius: 8px;
  top: 0;
  left: 0;
  pointer-events: none;
  object-fit: ${props => props.fit};
`;
